import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import DashboardHeader from "../components/DashboardHeader";
import StatsCards from "../components/StatsCards";
import InProgressCourses from "../components/InProgressCourses";

const BASE_URL = process.env.REACT_APP_API_BASE_URL

const DashboardPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [statsData, setStatsData] = useState({
    enrolledCourses: 0,
    activeCourses: 0,
    completedCourses: 0,
  });
  const [coursesData, setCoursesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);

  const user = useSelector((state) => state.auth.user);

  // Fetch profile photo separately
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem("access");
        const res = await axios.get(`${BASE_URL}/users/profile/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const p = res.data.profile_photo;
        if (p) {
          setProfilePhoto(
            p.startsWith("http") ? p : `${BASE_URL}${p}`
          );
        } else {
          setProfilePhoto(null);
        }
      } catch (e) {
        setProfilePhoto(null);
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("access");

        // Fetch dashboard stats
        const statsResponse = await axios.get(`${BASE_URL}/courses/dashboard/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStatsData(statsResponse.data);

        // Fetch enrolled courses
        const coursesResponse = await axios.get(`${BASE_URL}/courses/my-courses/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Fetch progress for each course
        const courseList = Array.isArray(coursesResponse.data)
          ? coursesResponse.data
          : coursesResponse.data.results || [];

        const coursesWithProgress = await Promise.all(
          courseList
            .filter((enrollment) => enrollment.status !== "completed")
            .map(async (enrollment) => {
              const courseDetailResponse = await axios.get(
                `${BASE_URL}/courses/${enrollment.course.id}/`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              return {
                id: enrollment.course.id,
                title: enrollment.course.title,
                image: enrollment.course.image || "/placeholder.svg",
                rating: enrollment.course.average_rating || 0,
                completedLessons: courseDetailResponse.data.progress.completed,
                totalLessons: courseDetailResponse.data.progress.total,
                progressPercentage: courseDetailResponse.data.progress.percentage,
              };
            })
        );

        setCoursesData(coursesWithProgress);
        setLoading(false);
      } catch (err) {
        setError("Failed to load dashboard data.");
        setLoading(false);
        //console.error(err);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  if (loading) {
    return <div className="text-center py-5"></div>;
  }

  if (error) {
    return <div className="text-center py-5 text-danger">{error}</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="d-flex">
        <Sidebar collapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed((prev) => !prev)} />
        <div className="main-content flex-grow-1">
          <DashboardHeader
            user={user}
            profilePhoto={profilePhoto}
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
          <div className="dashboard-content p-4">
            <h2 className="dashboard-title mb-4">Dashboard</h2>
            <StatsCards stats={statsData} />
            <InProgressCourses courses={coursesData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;